/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
*/
import { AfterViewInit, Component, ElementRef, inject, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppComponent } from '@src/app/app.component';
import { User } from '@src/app/model/user';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { DashboardChartComponent } from '@src/app/shared/dashboard/dashboard-widget/dashboard-widget.component';
import { ChosenWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { AvailableWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { ConfigureDashboardComponent } from '@src/app/shared/dashboard/configure-dashboard/configure-dashboard.component';
import { MatDialog } from '@angular/material/dialog';
import { EditWidgetDialogComponent } from '@src/app/shared/dashboard/edit-widget/edit-widget.component';

export interface ConfigureWidgetParms { event: MouseEvent, chosenWidget: ChosenWidget, changeWidget: (aw: AvailableWidget) => void }
@Component({
    selector: 'app-dashboard',
    imports: [DashboardChartComponent, MatButtonModule, MatMenuModule, MatIconModule],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnDestroy, AfterViewInit, OnChanges {
    @Input() dashboardName = 'unknown';
    @Input() defaultWidgets : ChosenWidget[] = [];
    @Input() allWidgets: AvailableWidget[] = [];
    host = inject(ElementRef);
    @ViewChild('dashboardDiv') dashboardDiv!: ElementRef;

    private currentUserSvc = inject(CurrentUserService);
    private router = inject(Router);
    private isNarrowSvc = inject(IsNarrowService);
    private activatedRoute = inject(ActivatedRoute);
    private resizeObserver!: ResizeObserver;

    currentUser: User;
    dialog = inject(MatDialog);
    ready = false;
    showingWidgets: ChosenWidget[] = [];
    chartHeight = 180;
    chartWidth = 180;

    autoRefreshIntervalId: number = null;
    refreshNumber = 0;

    subscriptions: Subscription[] = [];

    constructor() {
        const titleSvc = inject(Title);

        titleSvc.setTitle($localize`${AppComponent.appName} - Agent Dashboard`)
        this.subscriptions.push(this.currentUserSvc.getCurrentUser().subscribe(user => {
            this.currentUser = user;
            this.subscriptions.push(this.isNarrowSvc.resizeObservable$.subscribe(() => this.reDrawCharts()));
        }));
        this.subscriptions.push(this.activatedRoute.queryParams.subscribe( queryParams => {
            if (queryParams.edit) {
                this.configureDashboard()
            }            
        }))
        if (this.isNarrowSvc.isPhone()) {
            this.defaultWidgets.forEach( w => {
                w.widthWeight = Math.min(w.widthWeight, 2);
                w.heightWeight = Math.min(w.heightWeight, 2);
            })
        }
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }        
    }

    ngOnChanges(): void {
        this.getCharts();
    }

    toggleAutoRefresh() {
        if (this.autoRefreshIntervalId) {
            this.stopRefresh()
        } else {
            this.autoRefreshIntervalId = window.setInterval(this.autoRefresh.bind(this), 1000 * 60 * 5);
        }
    }

    autoRefresh() {
        this.refreshNumber += 1;
        const charts = this.showingCharts();
        const iTo = this.refreshNumber % charts.length
        this.showingCharts[iTo].chart.getData();
    }

    showingCharts() {
        const showingCharts: AvailableWidget[] = [];
        this.showingWidgets.forEach(cc => showingCharts.push(this.getChart(cc.code)));
        return showingCharts;
    }



    stopRefresh() {
        if (this.autoRefreshIntervalId) {
            window.clearInterval(this.autoRefreshIntervalId);
        }
        this.autoRefreshIntervalId = null;
        this.refreshNumber = 0;
    }

    ngAfterViewInit(): void {
        const firstChild = this.dashboardDiv.nativeElement.querySelector('div');
        if (!firstChild) return;

        this.resizeObserver = new ResizeObserver(() => this.reDrawCharts());

        this.resizeObserver.observe(firstChild);    
        this.reDrawCharts();
    }

    changeWidth(event: { chosen: ChosenWidget, widthChange: number }) {
        event.chosen.widthWeight += event.widthChange;
        setTimeout(() => this.reDrawCharts());
        //this.reDrawCharts();
    }

    changeHeight(event: { chosen: ChosenWidget, heightChange: number }) {
        console.log('Change height', event);
        if (!event.chosen.heightWeight) event.chosen.heightWeight = 1;
        event.chosen.heightWeight += event.heightChange;
        setTimeout(() => this.reDrawCharts());
    }

    getChart(chartCode): AvailableWidget {
        return this.allWidgets.find(ca => ca.code === chartCode);
    }

    canDeactivate() {
        this.stopRefresh();
        return true;
    }

    stillAvailCharts() {
        const stillAvail = this.allWidgets.slice();
        this.showingWidgets.forEach(cc => stillAvail.splice(stillAvail.findIndex(o => o.code === cc.code), 1));
        return stillAvail;
    }

    reDrawCharts() {
        this.showingWidgets.forEach(cc => {
            const w = this.getChart(cc.code);
            if (w.chart) {
                w.chart.reSize()
            }
        })

        this.saveCharts();
    }

    saveCharts() {
        localStorage.setItem(this.dashboardName, JSON.stringify(this.showingWidgets));
    }

    private getCharts() {
        let chartChoice = null
        const choiceString = localStorage.getItem(this.dashboardName);

        if (choiceString) {
            try {
                chartChoice = JSON.parse(choiceString);
                if (Array.isArray(chartChoice) && chartChoice.length === 2 && Array.isArray(chartChoice[0])) {
                    console.log('Old fashioned chart choices, go with default');
                    chartChoice = null;
                } else {
                    chartChoice.forEach(cc => {
                        if (!this.allWidgets.find(ac => ac.code === cc.code)) {
                            throw 'Saved option ' + cc.code + ' does not exist';
                        }
                        if (cc.widthWeight <= 0 || cc.widthWeight >= 100) {
                            throw 'Saved option ' + cc.code + ' has rubbish weight';
                        }
                    });
                }
            } catch (e) {
                console.error('Seems to be bad options saved...', e);
                chartChoice = null;
            }
        }

        if (chartChoice === null) {
            this.setDefaultWidgets();
        } else {
            this.showingWidgets = chartChoice;
        }
        this.ready = true;
    }

    setDefaultWidgets() {
        this.showingWidgets = this.defaultWidgets;
        this.reDrawCharts();
    }

    configureDashboard() {
        const dialogRef = this.dialog.open(ConfigureDashboardComponent, {
            width: '900px', // adjust as needed
            height: '50vh',
            minHeight: '480px',
            data: {
                allWidgets: this.allWidgets,
                chosenWidgets: this.showingWidgets,
                defaultWidgets: this.defaultWidgets,
                reDrawCharts: this.reDrawCharts.bind(this)
            }
        });

        dialogRef.afterClosed().subscribe(() => {
            setTimeout(() => this.reDrawCharts());
            this.router.navigate([], {
                queryParams: { edit: null },
                queryParamsHandling: 'merge',
            });            
        });
    }

    configureWidget(parms: ConfigureWidgetParms) {
        const dialogWidth = 400;
        // Left or right of widget?
        const offsetIfTooClose = dialogWidth + (parms.chosenWidget.widthWeight * 150);
        const viewportWidth = window.innerWidth;
        const clickX = parms.event.clientX;
        let dialogLeftPosition: string;
        if (clickX + dialogWidth >= viewportWidth) {
            const left = Math.max(clickX - offsetIfTooClose, 0);
            console.log('Placing left as ' + left);
            dialogLeftPosition = `${left}px`;
        } else {
            dialogLeftPosition = `${clickX}px`;
        }
        // Above or below?
        const top = `${parms.event.clientY}px`;
        const bottom = `${window.innerHeight - parms.event.clientY}px`;
        const above = (parms.event.clientY + 500 > window.innerHeight) && (window.innerHeight - parms.event.clientY - 500 > 0);

        this.dialog.open(EditWidgetDialogComponent, {
            width: `${dialogWidth}px`,
            position: {
                top : above ? null : window.innerHeight > parms.event.clientY + 500 ? top : null,
                bottom: above ? bottom : null,
                left: dialogLeftPosition,
            },
            data: {
                chosenWidget: parms.chosenWidget,
                showingWidgets: this.showingWidgets,
                allWidgets: this.allWidgets,
                changeWidget: parms.changeWidget,
                refreshWidgets: () => setTimeout(() => this.reDrawCharts()),
                widget: this.getChart(parms.chosenWidget.code)
            }
        }).afterClosed().subscribe( () => {
            setTimeout(()=> this.reDrawCharts())
        });
    }
}
